/**
 * A folha de impressão do resumo do banco de horas.
 *
 * Papel: reproduz `bhPrintDashboard` da origem na `FolhaDeImpressao` — o título
 * "Banco de Horas", a linha "Exportado em" com o instante do clique, e a caixa
 * "Saldo por Colaborador" com o nome, o saldo na cor da faixa e os dias.
 *
 * Como usar: as regras de montar, imprimir e desmontar são as da
 * `FolhaDeImpressao`. Quem monta passa a lista inteira do banco de horas, e é
 * aqui que ela vira só os ativos em ordem de nome, como no resumo da tela.
 */
import { type ColaboradorBancoHoras, faixaDoSaldo, saldoEmMinutos } from '@concierge/shared';

import { cn } from '@/shared/lib/utils';
import { TOM_DA_FAIXA, ativosPorNome, formatarDias, formatarHoras } from '../banco-horas';
import { CaixaDaFolha, FolhaDeImpressao } from './folha-de-impressao';
import {
  CLASSES_DO_CORPO_DA_FOLHA,
  CLASSES_DO_NUMERO_DA_FOLHA,
  CLASSES_DO_TH_DA_FOLHA,
  linhaDeExportacao,
} from './partes-da-folha';

interface FolhaResumoBancoHorasProps {
  /** A lista inteira do banco de horas, no momento do clique. */
  colaboradores: readonly ColaboradorBancoHoras[];
  /** O instante do clique em "🖨 Imprimir / PDF", em ISO 8601 com fuso. */
  exportadaEm: string;
  /** Chamado quando a janela de impressão fecha. */
  onConcluida: () => void;
}

/**
 * A folha do resumo.
 *
 * @param colaboradores - A lista inteira; os inativos ficam de fora.
 * @param exportadaEm - O instante do clique.
 * @param onConcluida - Chamado uma vez, no `afterprint`.
 * @returns A folha, escondida na tela e visível só na impressão.
 */
export function FolhaResumoBancoHoras({
  colaboradores,
  exportadaEm,
  onConcluida,
}: FolhaResumoBancoHorasProps) {
  const ativos = ativosPorNome(colaboradores);

  return (
    <FolhaDeImpressao
      titulo="Banco de Horas"
      subtitulo={linhaDeExportacao(exportadaEm)}
      onConcluida={onConcluida}
    >
      <CaixaDaFolha titulo="Saldo por Colaborador">
        <table aria-label="Saldo por Colaborador" className="w-full border-collapse">
          <thead>
            <tr>
              <th className={CLASSES_DO_TH_DA_FOLHA}>Colaborador</th>
              <th className={cn(CLASSES_DO_TH_DA_FOLHA, 'text-right')}>Saldo</th>
              <th className={cn(CLASSES_DO_TH_DA_FOLHA, 'text-right')}>Dias (aprox.)</th>
            </tr>
          </thead>
          <tbody className={CLASSES_DO_CORPO_DA_FOLHA}>
            {ativos.length === 0 ? (
              <tr>
                <td colSpan={3} className="p-6! text-center text-texto-fraco">
                  Nenhum colaborador cadastrado.
                </td>
              </tr>
            ) : (
              ativos.map((colaborador) => {
                const saldo = saldoEmMinutos(colaborador.meses);
                return (
                  <tr key={colaborador.id}>
                    <td className="font-semibold text-foreground">
                      {colaborador.nomeCompleto}
                    </td>
                    <td
                      className={cn(
                        CLASSES_DO_NUMERO_DA_FOLHA,
                        TOM_DA_FAIXA[faixaDoSaldo(saldo)],
                      )}
                    >
                      {formatarHoras(saldo)}
                    </td>
                    <td className="text-right font-mono text-xs/[1.6] whitespace-nowrap text-texto-medio">
                      {formatarDias(saldo)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </CaixaDaFolha>
    </FolhaDeImpressao>
  );
}
